import { useState, useCallback, useEffect, useRef } from "react"
import { SSE } from "utils/SSE"
import { ASSIGNED, ACTIVE } from "constants/statuses"
import MEDIA_SERVER_TYPES from "constants/mediaServerTypes"
import { LOGIN_DEFAULT_PRESENCE_APPLIED_KEY } from "constants/storageKeys"
import { getLoggerInstance } from "utils/logger"
import { dataService } from "utils/dataService"
import { parseTaskCallData } from "./utils/parseCallData"
import { usePureCallback } from "hooks/usePureCallback"
import { useAlertMessage } from "@cloudbreakus/ui-components"
import { useConfigFlag } from "@cloudbreakus/featurebang-react"
import {
  readNmsParticipant,
  forgetNmsParticipant,
  readSupervisorSession,
  discardInheritedEntries,
} from "./nmsParticipantStore"

const logger = getLoggerInstance()

const closeParticipant = (interactionId, participantId) => {
  if (!participantId || participantId === "NA") return
  dataService.Participant.hangUp({ interactionId, participantId }).catch((err) =>
    logger.debug(`Could not close the media participant ${participantId}`, err)
  )
}

export { LOGIN_DEFAULT_PRESENCE_APPLIED_KEY }

const markLoginDefaultPresenceApplied = () => {
  sessionStorage.setItem(LOGIN_DEFAULT_PRESENCE_APPLIED_KEY, "true")
}

const wasLoginDefaultPresenceApplied = () => Boolean(sessionStorage.getItem(LOGIN_DEFAULT_PRESENCE_APPLIED_KEY))

export const useInitialCallStatus = ({
  userId,
  callOperationRef,
  recoveringCallRef,
  handleRingingCall,
  setAnsweredCall,
  initializeCallScreen,
  answeredCall,
  SSEopen,
  setNmsParticipantId,
  expectingAnswer,
  nmsParticipantId,
  supervisorCall,
  setSupervisorCall,
  onSupervisorRejoinFailed,
}) => {
  const { showCustomToast } = useAlertMessage()
  const staleAssignedCallThresholdSeconds = useConfigFlag("calls.staleAssignedCallThresholdSeconds")
  const staleAssignedCallThresholdMs = staleAssignedCallThresholdSeconds
    ? staleAssignedCallThresholdSeconds * 1000
    : undefined

  // SSE can announce `connect` again while a status check is still joining. The state that gates a
  // check is only set once it finishes, so a second one would join the same call and replace the
  // same stale participant twice, or report a late failure over a restored call (AE-1582). Checks
  // are therefore serialised; an announcement that arrives meanwhile asks for one more look, which
  // only happens if the first found nothing.
  const checkInFlight = useRef(false)
  const recheckRequested = useRef(false)

  const [initialCallStatus, setInitialCallStatus] = useState(null) // true/false if call active/assigned on login or not.  null if not yet checked
  const [presenceInitiallySet, setPresenceInitiallySet] = useState(false)
  // True once the post-login presence is established (default applied, or a call is
  // already active). Used to suppress the presence display until it settles, so the
  // nav does not flicker through the worker's transient activity on login.
  const [loginPresenceSettled, setLoginPresenceSettled] = useState(false)

  const checkAssignedCall = useCallback(
    async (isCurrent) => {
      const response = await dataService.Calls.get({ agentId: userId, status: ASSIGNED })
      if (!isCurrent()) return true
      if (response?.data?.length) {
        const now = Date.now()
        const assignedCall = response.data.find((call) => {
          if (staleAssignedCallThresholdMs === undefined) {
            return true
          }
          const assignTime = new Date(call.assignTime)
          // Only consider calls assigned within the threshold
          return now - assignTime.getTime() <= staleAssignedCallThresholdMs
        })
        if (assignedCall) {
          logger.debug("ringing call found", assignedCall)
          handleRingingCall(parseTaskCallData(assignedCall, userId))
          return true
        } else {
          logger.debug("ringing call assigned too long ago", {
            assignedCalls: response.data,
          })
          return false
        }
      } else {
        logger.debug("no ringing calls")
        return false
      }
    },
    [handleRingingCall, userId, staleAssignedCallThresholdMs]
  )

  // Both reload paths replace the participant this tab orphaned. Player reconnect after Leave
  // Call has different semantics (AE-1587) and deliberately does not use this replacement path.
  //
  // The replacement relies on the API (martti-agent-api interaction.service.ts, createParticipant):
  // with `replaceParticipantId` it awaits the old participant's disconnect before it creates the new
  // one, and BARGE/LISTEN never reuse an existing participant. If the API were changed to hand back
  // the same id for those modes without disconnecting first, a rejoin could overlap the old
  // participant and need revisiting here.
  const recoverCall = useCallback(
    async (call, mode, stale) => {
      recoveringCallRef.current = {
        taskId: call.taskId,
        supervisor: Boolean(mode),
        operation: callOperationRef.current,
      }
      let participantId
      if (call.interaction.mediaServer === MEDIA_SERVER_TYPES.NMS) {
        const { data } = await dataService.Interactions.reconnect({
          agentId: userId,
          taskId: call.taskId,
          interactionId: call.interaction.id,
          languageId: call.language.id,
          ...(mode && { mode }),
          ...(stale && { replaceParticipantId: stale }),
        })
        if (!data?.participantId) throw new Error("Recovery did not return a media participant")
        participantId = data.participantId
      }
      return { call, participantId, stale, supervisor: Boolean(mode) }
    },
    [userId, callOperationRef, recoveringCallRef]
  )

  const checkActiveCall = useCallback(
    async (isCurrent) => {
      const response = await dataService.Calls.get({ agentId: userId, status: ACTIVE })
      if (!isCurrent() || !response?.data?.length) return null
      const call = parseTaskCallData(response.data[0], userId)
      try {
        return await recoverCall(call, undefined, readNmsParticipant(call.interaction.id))
      } catch (err) {
        if (err?.response?.status !== 404) throw err
        // An own task whose room has ended still takes priority over a monitoring session.
        logger.debug(`room for task ${call.taskId} not found - customer likely hung up`)
        return { call, participantId: "NA", supervisor: false }
      }
    },
    [userId, recoverCall]
  )

  const checkSupervisorSession = useCallback(
    async (session, stale, isCurrent) => {
      if (!session || !isCurrent()) return null
      const { mode, call } = session
      try {
        return await recoverCall(call, mode, stale)
      } catch (err) {
        if (!isCurrent()) return null
        logger.error(`Unable to rejoin ${mode} session on interaction ${call.interaction.id} after a refresh`, err)
        closeParticipant(call.interaction.id, stale)
        forgetNmsParticipant(call.interaction.id)
        onSupervisorRejoinFailed()
        return null
      }
    },
    [recoverCall, onSupervisorRejoinFailed]
  )

  const installRecovery = useCallback(
    ({ call, participantId, stale, supervisor }) => {
      if (participantId) setNmsParticipantId(participantId)
      if (supervisor) setSupervisorCall(true)
      initializeCallScreen()
      setAnsweredCall(call)
      if (stale && stale !== participantId) closeParticipant(call.interaction.id, stale)
    },
    [initializeCallScreen, setAnsweredCall, setNmsParticipantId, setSupervisorCall]
  )

  // Joining cannot be cancelled at the API, so a result a newer action has superseded is closed
  // rather than installed over that action or used to send the user home on its behalf.
  const disposeSuperseded = useCallback((recovered) => {
    if (recovered) closeParticipant(recovered.call.interaction.id, recovered.participantId)
  }, [])

  // The first answer wins: later checks never overwrite what initialisation already settled on.
  const settleStatus = useCallback((callStatus) => {
    setInitialCallStatus((initial) => (initial !== null ? initial : callStatus))
  }, [])

  const checkCallStatus = usePureCallback(async () => {
    // Skip JOIN while answering or after we already have an NMS participant.
    // SSE `connect` during that window would mint a second participant (AE-1352).
    // "NA" is the 404 sentinel, not a real participant.
    const callEstablished = Boolean(
      answeredCall || expectingAnswer || supervisorCall || (nmsParticipantId && nmsParticipantId !== "NA")
    )
    if (callEstablished || !userId) {
      if (callEstablished && userId) {
        // A call settled while a queued re-check was waiting, so there is nothing left to look for.
        // Without this the check that was meant to settle initialisation exits here and never does,
        // leaving the presence control on "Connecting". Not a login, so no default presence applies.
        settleStatus(true)
      }
      return null
    }

    if (checkInFlight.current) {
      recheckRequested.current = true
      return null
    }
    checkInFlight.current = true

    // A duplicated tab starts with the original's stored session. Drop it before the first lookup:
    // if that lookup fails, a later refresh of the duplicate would otherwise be taken for a genuine
    // one and rejoin, replacing the participant the original tab still uses.
    discardInheritedEntries()

    // A newer action permanently supersedes this lookup, even if that action has already ended
    // by the time the response arrives. CallContext increments synchronously, before any await.
    const operation = callOperationRef.current
    const isCurrent = () => callOperationRef.current === operation
    let callStatus
    let failed = false

    try {
      // Take the monitoring session as it stands now. One written while the lookups below are
      // pending belongs to a join the user just made by hand, and recovering it would replace
      // that live participant with a new one.
      const startupSession = readSupervisorSession(userId)
      // Identify monitoring before awaiting the own-task lookup: an empty own-task SSE snapshot
      // is normal for a supervisor and must not cancel recovery during that lookup either.
      recoveringCallRef.current = { supervisor: Boolean(startupSession), operation }
      const startupParticipant = startupSession ? readNmsParticipant(startupSession.call.interaction.id) : null

      let recovered = await checkActiveCall(isCurrent)
      if (!recovered) recovered = await checkSupervisorSession(startupSession, startupParticipant, isCurrent)
      if (!isCurrent()) {
        disposeSuperseded(recovered)
        callStatus = true
      } else if (recovered) {
        installRecovery(recovered)
        callStatus = true
      } else {
        callStatus = await checkAssignedCall(isCurrent)
      }
    } catch (err) {
      if (!isCurrent()) {
        callStatus = true
      } else {
        failed = true
        logger.error("checking call status failed", err)
        showCustomToast({
          level: "error",
          message: "Checking your current call status failed",
          onRetry: checkCallStatus,
        })
      }
    } finally {
      recoveringCallRef.current = null
      checkInFlight.current = false
    }

    const recheck = recheckRequested.current
    recheckRequested.current = false

    if (failed) {
      // A connection announced while this lookup was pending has had no lookup of its own, so the
      // failure must not swallow it.
      if (recheck) {
        return checkCallStatus()
      }
      return
    }

    if (recheck && !callStatus) {
      return checkCallStatus()
    }

    settleStatus(callStatus)
  })

  useEffect(() => {
    if (SSEopen) {
      SSE.on("connect", checkCallStatus)

      return () => {
        SSE.off("connect", checkCallStatus)
      }
    }
  }, [SSEopen, checkCallStatus])

  useEffect(() => {
    if (presenceInitiallySet || initialCallStatus === null || !SSEopen) {
      return
    }

    // Active/assigned call: settle immediately — do not override presence mid-call.
    if (initialCallStatus) {
      setPresenceInitiallySet(true)
      markLoginDefaultPresenceApplied()
      setLoginPresenceSettled(true)
      return
    }

    // Refresh / later loads in this browser session: leave presence alone (AE-1051).
    if (wasLoginDefaultPresenceApplied()) {
      setPresenceInitiallySet(true)
      setLoginPresenceSettled(true)
      return
    }

    setPresenceInitiallySet(true)

    // First load after login only: API sets Not Ready via login-default (AE-1265).
    // Only mark the session applied on success so a failed call can retry after refresh.
    dataService.Presence.setLoginDefault({ agentId: userId })
      .then(() => {
        markLoginDefaultPresenceApplied()
      })
      .catch((err) => logger.error("Failed to set default presence on login", err))
      .finally(() => {
        setLoginPresenceSettled(true)
      })
  }, [presenceInitiallySet, initialCallStatus, SSEopen, userId])

  return { initialCallStatusChecked: initialCallStatus !== null, loginPresenceSettled }
}
